import { Suspense, lazy, useEffect, useState } from 'react'
import { Network } from 'lucide-react'
import { AgentTeamCompactList } from '@/features/agents-marketing/components/AgentTeamCompactList'
import { AGENT_TEAM_ID } from '@/features/home/data/homeContent'

/* React Flow measures the DOM, so the canvas only mounts on the client and
   its chunk stays out of the initial bundle. The skeleton reserves the same
   height so nothing shifts when it arrives. */
const AgentCanvasPreviewInner = lazy(
  () =>
    import('@/features/agents-marketing/components/AgentCanvasPreviewInner'),
)

const CANVAS_HEIGHT = 'h-[420px]'
/* Below md the canvas would have to shrink to ~0.4x to fit, so a stacked
   list stands in (see AgentTeamCompactList) and React Flow isn't mounted. */
const CANVAS_MEDIA_QUERY = '(min-width: 768px)'

function CanvasSkeleton() {
  return (
    <div
      aria-hidden
      className={`${CANVAS_HEIGHT} relative`}
      style={{
        backgroundImage:
          'radial-gradient(var(--color-border-strong) 1px, transparent 1px)',
        backgroundSize: '16px 16px',
      }}
    >
      <div className="absolute top-8 left-1/2 h-24 w-[220px] -translate-x-1/2 rounded-xl border border-primary/40 bg-[var(--color-bg-primary)] motion-safe:animate-pulse" />
      <div className="absolute bottom-8 left-1/2 flex -translate-x-1/2 gap-6">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-24 w-[140px] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-primary)] motion-safe:animate-pulse"
          />
        ))}
      </div>
    </div>
  )
}

/**
 * The example team on a real React Flow canvas, framed the way the builder
 * frames it. The nodes can be dragged so the "it is a canvas" claim is
 * something the reader can feel, not just read.
 */
export function AgentCanvasPreview() {
  const [showCanvas, setShowCanvas] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(CANVAS_MEDIA_QUERY)
    const update = () => setShowCanvas(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  return (
    <div className="surface-bevel w-full overflow-hidden rounded-[1.75rem]">
      <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-5 py-3.5">
        <Network className="size-4 shrink-0 text-primary" strokeWidth={1.75} />
        <span className="truncate font-mono text-caption text-[var(--color-text-secondary)]">
          agents / {AGENT_TEAM_ID}
        </span>
        <span className="eyebrow ml-auto shrink-0 whitespace-nowrap text-[10px] text-primary">
          Canvas
        </span>
      </div>

      <div className="md:hidden">
        <AgentTeamCompactList />
      </div>
      <div
        className={`${CANVAS_HEIGHT} hidden bg-[var(--color-bg-primary)]/50 md:block`}
      >
        {showCanvas ? (
          <Suspense fallback={<CanvasSkeleton />}>
            <AgentCanvasPreviewInner />
          </Suspense>
        ) : (
          <CanvasSkeleton />
        )}
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1.5 border-t border-[var(--color-border)] px-5 py-3.5 text-caption">
        <div className="flex items-center gap-2">
          <dt
            aria-hidden
            className="h-px w-4 bg-[var(--color-border-strong)]"
          />
          <dd className="text-[var(--color-text-secondary)]">
            Advises
            <span className="text-[var(--color-text-tertiary)]">
              {' '}
              · coordinator writes the reply
            </span>
          </dd>
        </div>
        <div className="flex items-center gap-2">
          <dt
            aria-hidden
            className="h-px w-4 bg-[var(--color-info)]"
          />
          <dd className="text-[var(--color-text-secondary)]">
            Delegates
            <span className="text-[var(--color-text-tertiary)]">
              {' '}
              · reply is sent as written
            </span>
          </dd>
        </div>
      </dl>
    </div>
  )
}
